import { useActivityDetector } from "./useActivityDetector";
import { useCryptoDrift } from "./useCryptoDrift";
import { useFilterStream } from "./useFilterStream";
import { useFunding } from "./useFunding";
import { useManipulationMonitor } from "./useManipulationMonitor";
import { useSpotFuturesSpread } from "./useSpotFuturesSpread";

export function useFilterData<T extends { symbol: string }>(data: T[]) {
  const { data: adtv15m } = useFilterStream("15m", "adtv");
  const { data: adtv30m } = useFilterStream("30m", "adtv");
  const { data: adtv1h } = useFilterStream("1h", "adtv");
  const { data: adtv4h } = useFilterStream("4h", "adtv");
  const { data: adtv1d } = useFilterStream("1d", "adtv");
  const { data: adtv1w } = useFilterStream("1w", "adtv");
  const { data: adtv1M } = useFilterStream("1M", "adtv");
  const { data: volume15m } = useFilterStream("15m", "volume");
  const { data: volume30m } = useFilterStream("30m", "volume");
  const { data: volume1h } = useFilterStream("1h", "volume");
  const { data: volume4h } = useFilterStream("4h", "volume");
  const { data: volume1d } = useFilterStream("1d", "volume");
  const { data: volume1w } = useFilterStream("1w", "volume");
  const { data: volume1M } = useFilterStream("1M", "volume");
  const { data: fundingData } = useFunding();
  const { data: spotFuturesSpread } = useSpotFuturesSpread();
  const { data: cryptoDrift } = useCryptoDrift();
  const { data: activityDetector } = useActivityDetector();
  const { data: manipulationMonitor } = useManipulationMonitor();

  if (
    !adtv15m ||
    !adtv30m ||
    !adtv1h ||
    !adtv4h ||
    !adtv1d ||
    !adtv1w ||
    !adtv1M ||
    !volume15m ||
    !volume30m ||
    !volume1h ||
    !volume4h ||
    !volume1d ||
    !volume1w ||
    !fundingData ||
    !volume1M ||
    !spotFuturesSpread
  ) {
    return data;
  }

  return data.map((d) => {
    const cryptoDriftData = cryptoDrift?.data?.find(
      (item) => item.symbol === d.symbol,
    );
    const activityDetectorData = activityDetector?.data?.find(
      (item) => item.symbol === d.symbol,
    );
    const manipulationMonitorData = manipulationMonitor?.find(
      (item) => item.symbol === d.symbol,
    );
    const spotFuturesSpreadData = spotFuturesSpread?.find(
      (item) => item.symbol === d.symbol,
    );
    return {
      ...d,
      cryptoDrift: cryptoDriftData?.value ?? 0,
      activityDetector: activityDetectorData?.value ?? 0,
      manipulationMonitorcorr: manipulationMonitorData?.corr ?? 0,
      manipulationMonitorcorrV: manipulationMonitorData?.corrV ?? 0,
      adtv15m: adtv15m[d.symbol] ?? {},
      adtv30m: adtv30m[d.symbol] ?? {},
      adtv1h: adtv1h[d.symbol] ?? {},
      adtv4h: adtv4h[d.symbol] ?? {},
      adtv1d: adtv1d[d.symbol] ?? {},
      adtv1w: adtv1w[d.symbol] ?? {},
      adtv1M: adtv1M[d.symbol] ?? {},
      volume15m: volume15m[d.symbol] ?? {},
      volume30m: volume30m[d.symbol] ?? {},
      volume1h: volume1h[d.symbol] ?? {},
      volume4h: volume4h[d.symbol] ?? {},
      volume1d: volume1d[d.symbol] ?? {},
      volume1w: volume1w[d.symbol] ?? {},
      volume1M: volume1M[d.symbol] ?? {},
      funding: fundingData[d.symbol] * 100,
      spread: spotFuturesSpreadData?.value
        ? spotFuturesSpreadData.value * -1
        : 0,
    };
  });
}
